import { z } from 'zod';
import { scanningMediaSemanticSnapshotSchema, scanningMediaStableKeySchema, } from './scanning-media-semantic-geometry.contract.js';
export const scanningMediaScanAssemblyCapability = 'scanning-media.scan-assembly.v1';
const assemblyCoordinateSchema = z.number().int().min(-1_000_000).max(1_000_000);
export const scanningMediaAssemblyPlacementSchema = z.strictObject({
    roomKey: scanningMediaStableKeySchema,
    floorKey: scanningMediaStableKeySchema,
    translation: z.strictObject({
        xMm: assemblyCoordinateSchema,
        yMm: assemblyCoordinateSchema,
        zMm: assemblyCoordinateSchema,
    }),
    rotationMilliDegrees: z.number().int().min(-360_000).max(360_000),
    source: z.enum(['semantic-baseline', 'deterministic', 'manual', 'import']),
    locked: z.boolean().default(false),
});
export const scanningMediaAssemblyLinkSchema = z.strictObject({
    key: scanningMediaStableKeySchema,
    sourceRoomKey: scanningMediaStableKeySchema,
    targetRoomKey: scanningMediaStableKeySchema,
    type: z.enum(['door', 'opening', 'shared-wall', 'stairs', 'hallway', 'adjacent', 'manual']),
    source: z.enum(['semantic-geometry', 'operator-review', 'import']),
    requiresTouch: z.boolean().default(true),
});
export const scanningMediaAssemblyDraftSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaScanAssemblyCapability),
    sourceVersionId: z.string().uuid(),
    placements: z.array(scanningMediaAssemblyPlacementSchema).min(1).max(250),
    links: z.array(scanningMediaAssemblyLinkSchema).max(1_000).default([]),
});
export const scanningMediaAssemblyValidationIssueSchema = z.strictObject({
    severity: z.enum(['info', 'warning', 'error']),
    code: z.enum([
        'ROOM_MISSING_PLACEMENT',
        'ROOM_REFERENCE_MISSING',
        'ROOM_FLOOR_MISMATCH',
        'ROOM_PLACEMENT_DUPLICATE',
        'ROOM_PLACEMENT_OVERLAP',
        'ROOM_LINK_REFERENCE_MISSING',
        'ROOM_LINK_NOT_TOUCHING',
        'ROOM_GRAPH_DISCONNECTED',
    ]),
    roomKeys: z.array(scanningMediaStableKeySchema).max(2),
    message: z.string().trim().min(1).max(500),
});
export const scanningMediaAssemblyTouchEdgeSchema = z.strictObject({
    sourceRoomKey: scanningMediaStableKeySchema,
    targetRoomKey: scanningMediaStableKeySchema,
    boundaryDistanceMm: z.number().int().nonnegative(),
    sharedBoundaryMm: z.number().int().nonnegative(),
});
export const scanningMediaAssemblyValidationSchema = z.strictObject({
    ok: z.boolean(),
    roomCount: z.number().int().nonnegative(),
    placementCount: z.number().int().nonnegative(),
    componentCount: z.number().int().nonnegative(),
    components: z.array(z.array(scanningMediaStableKeySchema).min(1)).max(250),
    touchEdges: z.array(scanningMediaAssemblyTouchEdgeSchema).max(31_125),
    issues: z.array(scanningMediaAssemblyValidationIssueSchema).max(10_000),
    errorCount: z.number().int().nonnegative(),
    warningCount: z.number().int().nonnegative(),
});
export const scanningMediaAssemblySnapshotSchema = scanningMediaAssemblyDraftSchema.extend({
    validation: scanningMediaAssemblyValidationSchema,
});
export const scanningMediaAssemblyAcceptanceSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    revisionId: z.string().uuid(),
    semanticVersionId: z.string().uuid(),
    reason: z.string().trim().min(10).max(500),
    acceptedByMembershipId: z.string().uuid(),
    acceptedAt: z.string().datetime({ offset: true }),
});
export const scanningMediaAssemblyRevisionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    revisionNumber: z.number().int().positive(),
    sourceVersionId: z.string().uuid(),
    assembly: scanningMediaAssemblySnapshotSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(10).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const scanningMediaAssemblyStateSchema = z.strictObject({
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    latestRevisionId: z.string().uuid().nullable(),
    acceptedRevisionId: z.string().uuid().nullable(),
    acceptedSemanticVersionId: z.string().uuid().nullable(),
    acceptedAuthority: z.enum(['none', 'current', 'stale']),
    latestRevision: scanningMediaAssemblyRevisionSchema.nullable(),
    acceptedRevision: scanningMediaAssemblyRevisionSchema.nullable(),
    acceptance: scanningMediaAssemblyAcceptanceSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const scanningMediaAssemblyCommandBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    captureId: z.string().uuid(),
    expectedAssemblyRevision: z.number().int().nonnegative(),
    reason: z.string().trim().min(10).max(500),
});
export const scanningMediaSaveAssemblyDraftCommandSchema = scanningMediaAssemblyCommandBaseSchema.extend({
    kind: z.literal('save-draft'),
    sourceVersionId: z.string().uuid(),
    placements: z.array(scanningMediaAssemblyPlacementSchema).min(1).max(250),
    links: z.array(scanningMediaAssemblyLinkSchema).max(1_000).default([]),
});
export const scanningMediaAcceptAssemblyRevisionCommandSchema = scanningMediaAssemblyCommandBaseSchema.extend({
    kind: z.literal('accept-revision'),
    expectedAssemblyRevision: z.number().int().positive(),
    revisionId: z.string().uuid(),
});
export const scanningMediaAssemblyCommandSchema = z.discriminatedUnion('kind', [
    scanningMediaSaveAssemblyDraftCommandSchema,
    scanningMediaAcceptAssemblyRevisionCommandSchema,
]);
export const scanningMediaAssemblyStateResponseSchema = z.strictObject({
    data: scanningMediaAssemblyStateSchema.nullable(),
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const scanningMediaAssemblyRevisionsResponseSchema = z.strictObject({
    data: z.array(scanningMediaAssemblyRevisionSchema),
});
function orientation(a, b, c) {
    return (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
}
function pointOnSegment(point, segment) {
    return Math.abs(orientation(segment.start, segment.end, point)) < 0.5 &&
        point.x >= Math.min(segment.start.x, segment.end.x) &&
        point.x <= Math.max(segment.start.x, segment.end.x) &&
        point.z >= Math.min(segment.start.z, segment.end.z) &&
        point.z <= Math.max(segment.start.z, segment.end.z);
}
function properIntersection(left, right) {
    const one = orientation(left.start, left.end, right.start);
    const two = orientation(left.start, left.end, right.end);
    const three = orientation(right.start, right.end, left.start);
    const four = orientation(right.start, right.end, left.end);
    return ((one > 0 && two < 0) || (one < 0 && two > 0)) &&
        ((three > 0 && four < 0) || (three < 0 && four > 0));
}
function pointInsidePolygon(point, polygon) {
    let inside = false;
    for (const [index, current] of polygon.entries()) {
        const next = polygon[(index + 1) % polygon.length];
        if (next === undefined)
            continue;
        if (pointOnSegment(point, { start: current, end: next }))
            return false;
        const crosses = (current.z > point.z) !== (next.z > point.z) &&
            point.x < ((next.x - current.x) * (point.z - current.z)) /
                (next.z - current.z) + current.x;
        if (crosses)
            inside = !inside;
    }
    return inside;
}
function polygonSegments(polygon) {
    return polygon.map((start, index) => {
        const end = polygon[(index + 1) % polygon.length];
        if (end === undefined)
            throw new Error('Assembly polygon is incomplete.');
        return { start, end };
    });
}
function pointSegmentDistance(point, segment) {
    const dx = segment.end.x - segment.start.x;
    const dz = segment.end.z - segment.start.z;
    const lengthSquared = dx * dx + dz * dz;
    if (lengthSquared === 0)
        return Math.hypot(point.x - segment.start.x, point.z - segment.start.z);
    const projection = Math.max(0, Math.min(1, ((point.x - segment.start.x) * dx + (point.z - segment.start.z) * dz) /
        lengthSquared));
    return Math.hypot(point.x - (segment.start.x + projection * dx), point.z - (segment.start.z + projection * dz));
}
function segmentDistance(left, right) {
    if (properIntersection(left, right) ||
        pointOnSegment(left.start, right) || pointOnSegment(left.end, right) ||
        pointOnSegment(right.start, left) || pointOnSegment(right.end, left))
        return 0;
    return Math.min(pointSegmentDistance(left.start, right), pointSegmentDistance(left.end, right), pointSegmentDistance(right.start, left), pointSegmentDistance(right.end, left));
}
function sharedBoundaryLength(left, right, toleranceMm) {
    const leftDx = left.end.x - left.start.x;
    const leftDz = left.end.z - left.start.z;
    const rightDx = right.end.x - right.start.x;
    const rightDz = right.end.z - right.start.z;
    const leftLength = Math.hypot(leftDx, leftDz);
    const rightLength = Math.hypot(rightDx, rightDz);
    if (leftLength === 0 || rightLength === 0 || segmentDistance(left, right) > toleranceMm)
        return 0;
    const normalizedCross = Math.abs(leftDx * rightDz - leftDz * rightDx) /
        (leftLength * rightLength);
    if (normalizedCross > 0.02)
        return 0;
    const useX = Math.abs(leftDx) >= Math.abs(leftDz);
    const leftRange = useX
        ? [Math.min(left.start.x, left.end.x), Math.max(left.start.x, left.end.x)]
        : [Math.min(left.start.z, left.end.z), Math.max(left.start.z, left.end.z)];
    const rightRange = useX
        ? [Math.min(right.start.x, right.end.x), Math.max(right.start.x, right.end.x)]
        : [Math.min(right.start.z, right.end.z), Math.max(right.start.z, right.end.z)];
    const projectedOverlap = Math.max(0, Math.min(leftRange[1] ?? 0, rightRange[1] ?? 0) -
        Math.max(leftRange[0] ?? 0, rightRange[0] ?? 0));
    const axisScale = useX ? Math.abs(leftDx) / leftLength : Math.abs(leftDz) / leftLength;
    return axisScale === 0 ? 0 : Math.round(projectedOverlap / axisScale);
}
function polygonsOverlap(left, right) {
    if (polygonSegments(left).some((leftSegment) => polygonSegments(right).some((rightSegment) => properIntersection(leftSegment, rightSegment)))) {
        return true;
    }
    const samples = (polygon) => {
        const segments = polygonSegments(polygon);
        const centroid = polygon.reduce((sum, point) => ({
            x: sum.x + point.x / polygon.length,
            z: sum.z + point.z / polygon.length,
        }), { x: 0, z: 0 });
        return [
            ...polygon,
            ...segments.map(({ start, end }) => ({
                x: (start.x + end.x) / 2,
                z: (start.z + end.z) / 2,
            })),
            centroid,
        ];
    };
    return samples(left).some((point) => pointInsidePolygon(point, right)) ||
        samples(right).some((point) => pointInsidePolygon(point, left));
}
function touchDetails(left, right) {
    let minimumDistance = Number.POSITIVE_INFINITY;
    let maximumSharedBoundary = 0;
    for (const leftSegment of polygonSegments(left)) {
        for (const rightSegment of polygonSegments(right)) {
            minimumDistance = Math.min(minimumDistance, segmentDistance(leftSegment, rightSegment));
            maximumSharedBoundary = Math.max(maximumSharedBoundary, sharedBoundaryLength(leftSegment, rightSegment, 5_000));
        }
    }
    return {
        boundaryDistanceMm: Math.round(minimumDistance),
        sharedBoundaryMm: maximumSharedBoundary,
    };
}
function placedPolygon(room, placement) {
    const center = room.floorPolygon.reduce((value, point) => ({
        x: value.x + point.xMm / room.floorPolygon.length,
        z: value.z + point.zMm / room.floorPolygon.length,
    }), { x: 0, z: 0 });
    const angle = placement.rotationMilliDegrees / 1_000 * Math.PI / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    return room.floorPolygon.map((point) => {
        const x = point.xMm - center.x;
        const z = point.zMm - center.z;
        return {
            x: Math.round(center.x + x * cosine - z * sine + placement.translation.xMm),
            z: Math.round(center.z + x * sine + z * cosine + placement.translation.zMm),
        };
    });
}
export function validateScanningMediaAssembly(semanticInput, draftInput, policy) {
    const semantic = scanningMediaSemanticSnapshotSchema.parse(semanticInput);
    const draft = scanningMediaAssemblyDraftSchema.parse(draftInput);
    if (!Number.isSafeInteger(policy.touchToleranceMm) || policy.touchToleranceMm < 0 ||
        policy.touchToleranceMm > 5_000 ||
        !Number.isSafeInteger(policy.minimumSharedBoundaryMm) ||
        policy.minimumSharedBoundaryMm < 0 || policy.minimumSharedBoundaryMm > 50_000)
        throw new Error('Assembly validation policy is outside supported bounds.');
    const issues = [];
    const rooms = semantic.floors.flatMap((floor) => floor.rooms.map((room) => ({ floorKey: floor.key, room })));
    const roomByKey = new Map(rooms.map((entry) => [entry.room.key, entry]));
    const placementByRoom = new Map();
    for (const placement of draft.placements) {
        if (placementByRoom.has(placement.roomKey)) {
            issues.push({ severity: 'error', code: 'ROOM_PLACEMENT_DUPLICATE',
                roomKeys: [placement.roomKey], message: 'Each room may have one assembly placement.' });
            continue;
        }
        placementByRoom.set(placement.roomKey, placement);
        const room = roomByKey.get(placement.roomKey);
        if (room === undefined) {
            issues.push({ severity: 'error', code: 'ROOM_REFERENCE_MISSING',
                roomKeys: [placement.roomKey], message: 'Assembly placement references a missing room.' });
        }
        else if (room.floorKey !== placement.floorKey) {
            issues.push({ severity: 'error', code: 'ROOM_FLOOR_MISMATCH',
                roomKeys: [placement.roomKey], message: 'Assembly placement floor does not match semantic evidence.' });
        }
    }
    for (const { room } of rooms) {
        if (!placementByRoom.has(room.key)) {
            issues.push({ severity: 'error', code: 'ROOM_MISSING_PLACEMENT', roomKeys: [room.key],
                message: 'Every retained semantic room requires an assembly placement.' });
        }
    }
    const validPlaced = rooms.flatMap(({ floorKey, room }) => {
        const placement = placementByRoom.get(room.key);
        return placement === undefined || placement.floorKey !== floorKey
            ? []
            : [{ floorKey, roomKey: room.key, polygon: placedPolygon(room, placement) }];
    });
    const touchEdges = [];
    for (const [index, left] of validPlaced.entries()) {
        for (const right of validPlaced.slice(index + 1)) {
            if (left.floorKey !== right.floorKey)
                continue;
            if (polygonsOverlap(left.polygon, right.polygon)) {
                issues.push({ severity: 'error', code: 'ROOM_PLACEMENT_OVERLAP',
                    roomKeys: [left.roomKey, right.roomKey],
                    message: 'Placed room polygons overlap and cannot be accepted.' });
                continue;
            }
            const details = touchDetails(left.polygon, right.polygon);
            if (details.boundaryDistanceMm <= policy.touchToleranceMm &&
                details.sharedBoundaryMm >= policy.minimumSharedBoundaryMm) {
                touchEdges.push({ sourceRoomKey: left.roomKey, targetRoomKey: right.roomKey, ...details });
            }
        }
    }
    const linkKeys = new Set();
    for (const link of draft.links) {
        const duplicate = linkKeys.has(link.key);
        linkKeys.add(link.key);
        const source = roomByKey.get(link.sourceRoomKey);
        const target = roomByKey.get(link.targetRoomKey);
        if (duplicate || source === undefined || target === undefined || source === target) {
            issues.push({ severity: 'error', code: 'ROOM_LINK_REFERENCE_MISSING',
                roomKeys: [link.sourceRoomKey, link.targetRoomKey],
                message: duplicate
                    ? 'Assembly link keys must be unique.'
                    : 'Assembly link must reference two distinct retained rooms.' });
            continue;
        }
        const touching = touchEdges.some((edge) => (edge.sourceRoomKey === link.sourceRoomKey && edge.targetRoomKey === link.targetRoomKey) ||
            (edge.sourceRoomKey === link.targetRoomKey && edge.targetRoomKey === link.sourceRoomKey));
        if (link.requiresTouch && !touching) {
            issues.push({ severity: 'error', code: 'ROOM_LINK_NOT_TOUCHING',
                roomKeys: [link.sourceRoomKey, link.targetRoomKey],
                message: 'A source-backed room link requires connected touching boundaries.' });
        }
    }
    const adjacency = new Map(rooms.map(({ room }) => [room.key, new Set()]));
    for (const edge of touchEdges) {
        adjacency.get(edge.sourceRoomKey)?.add(edge.targetRoomKey);
        adjacency.get(edge.targetRoomKey)?.add(edge.sourceRoomKey);
    }
    const unvisited = new Set(adjacency.keys());
    const components = [];
    while (unvisited.size > 0) {
        const first = [...unvisited].sort()[0];
        if (first === undefined)
            break;
        const component = [];
        const queue = [first];
        unvisited.delete(first);
        while (queue.length > 0) {
            const current = queue.shift();
            if (current === undefined)
                continue;
            component.push(current);
            for (const neighbor of [...(adjacency.get(current) ?? [])].sort()) {
                if (unvisited.delete(neighbor))
                    queue.push(neighbor);
            }
        }
        components.push(component.sort());
    }
    if (components.length > 1) {
        issues.push({
            severity: policy.requireSingleConnectedComponent ? 'error' : 'warning',
            code: 'ROOM_GRAPH_DISCONNECTED',
            roomKeys: [],
            message: 'The assembly contains separate room components; retained evidence identifies each component.',
        });
    }
    const errorCount = issues.filter(({ severity }) => severity === 'error').length;
    const warningCount = issues.filter(({ severity }) => severity === 'warning').length;
    return scanningMediaAssemblyValidationSchema.parse({
        ok: errorCount === 0,
        roomCount: rooms.length,
        placementCount: placementByRoom.size,
        componentCount: components.length,
        components,
        touchEdges,
        issues,
        errorCount,
        warningCount,
    });
}
//# sourceMappingURL=scanning-media-scan-assembly.contract.js.map